'use client'

import DateRangeFilter from '@/components/date-range-filter'
import { useServiceLabel } from '@/lib/hooks/use-service-label'
import ListSortSelect, { AppointmentSort, AppointmentSortOrder } from '@/components/list-sort-select'
import { ClientSelector } from '@/components/client/ClientSelector'
import { PartnerSelector } from '@/components/partner/PartnerSelector'
import { ApiResponse } from '@/lib/api/request'
import {
    Exam,
    getExamsByCompany,
    getExamStatusColor,
    getExamStatusLabel,
} from '@/lib/api/exams/exam-api'
import {
    getOrdersByCompany,
    getOrderStatusColor,
    getOrderStatusLabel,
    Order,
} from '@/lib/api/orders/order-api'
import { useDebounce } from '@/lib/hooks/use-debounce'
import { usePaginatedPage } from '@/lib/hooks/use-paginated-page'
import { useUrlPage } from '@/lib/hooks/use-url-page'
import PaginationControls from '@/components/pagination/PaginationControls'
import {
    FilterField,
    ListEmptyState,
    ListFilterBar,
    ListPageHeader,
} from '@/components/list-page'
import { useAuth } from '@/store/hooks'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { useCallback, useMemo, useState } from 'react'
import {
    MdAccessTime,
    MdArrowForward,
    MdAdd,
    MdSell,
    MdBusiness,
    MdHealthAndSafety,
    MdInventory2,
    MdPerson,
} from 'react-icons/md'
import { useNavigate } from 'react-router-dom'
import { useClientLabel } from '@/lib/hooks/use-client-label'

/**
 * Um pacote agendado não tem tabela própria: ele é gravado como Exam OU Order (roteado por
 * composição) e identificado por `packageCatalogId`. Não há endpoint agregador, então a tela busca
 * os dois com `packages=only`, mescla e pagina no cliente. Ver docs/catalog-packages.md.
 */
/**
 * O mesmo filtro de status é enviado aos DOIS endpoints, e os enums de exame e
 * procedimento não coincidem (exame tem REQUESTED, procedimento tem SCHEDULED e
 * POSTPONED). Só os status comuns aos dois podem ser oferecidos: um valor exclusivo
 * de um deles seria inválido no outro e a lista voltaria pela metade.
 */
const PACKAGE_STATUS_OPTIONS = {
    BUDGET: 'Orçamento',
    IN_PROGRESS: 'Em Andamento',
    COMPLETED: 'Concluído',
    CANCELLED: 'Cancelado',
} as const

type PackageStatusFilter = keyof typeof PACKAGE_STATUS_OPTIONS

type PackageRow = {
    id: number
    origin: 'EXAM' | 'PROCEDURE'
    name: string
    clientName?: string
    partnerName?: string
    healthPlanName?: string
    statusLabel: string
    statusColor: string
    /** Data do atendimento — o que a linha exibe. */
    date: string
    createdAt: string
    updatedAt: string
    price: number
    detailPath: string
}

const toRowFromExam = (exam: Exam): PackageRow => ({
    id: exam.id,
    origin: 'EXAM',
    name: exam.packageCatalog?.name || exam.title,
    clientName: exam.client?.name,
    partnerName: exam.partner?.name || exam.partner?.tradingName,
    healthPlanName: exam.healthPlan?.name,
    statusLabel: getExamStatusLabel(exam.status, exam.scheduledAt),
    statusColor: getExamStatusColor(exam.status, exam.scheduledAt),
    date: exam.requestedAt,
    createdAt: exam.createdAt,
    updatedAt: exam.updatedAt,
    price: exam.finalPrice || exam.totalPrice || 0,
    detailPath: `/bookings/exams/${exam.id}`,
})

const toRowFromOrder = (order: Order): PackageRow => ({
    id: order.id,
    origin: 'PROCEDURE',
    name: order.packageCatalog?.name || order.catalog?.name || 'Pacote',
    clientName: order.client?.name,
    partnerName: order.partner?.name || order.partner?.tradingName,
    healthPlanName: order.healthPlan?.name,
    statusLabel: getOrderStatusLabel(
        order.status,
        order.scheduledAt
    ),
    statusColor: getOrderStatusColor(
        order.status,
        order.scheduledAt
    ),
    date: order.scheduledAt || order.createdAt,
    createdAt: order.createdAt,
    updatedAt: order.updatedAt,
    price: order.totalPrice || 0,
    detailPath: `/bookings/orders/${order.id}`,
})

export default function PackagesPage() {
    const { user, isPartner, isEmployee } = useAuth()
    const serviceLabels = useServiceLabel()
    const { ClientLabel } = useClientLabel()
    const hidePartnerSelector = isPartner || (isEmployee && !!user?.partnerId)
    const navigate = useNavigate()
    const [searchInput, setSearchInput] = useState('')
    const [filterById, setFilterById] = useState('')
    const [clientFilter, setClientFilter] = useState<number | null>(null)
    const [partnerFilter, setPartnerFilter] = useState<number | null>(null)
    const [statusFilter, setStatusFilter] = useState<PackageStatusFilter | ''>('')
    const [dateRange, setDateRange] = useState<{ startDate?: string; endDate?: string }>({})
    // Criação como padrão: o operador abre a lista para achar o que acabou de
    // registrar, não o que está marcado para a semana que vem.
    const [sortBy, setSortBy] = useState<AppointmentSort>('created')
    // Direção separada do critério: o operador escolhe entre começar pelo mais
    // recente (padrão) ou pelo mais antigo.
    const [sortOrder, setSortOrder] = useState<AppointmentSortOrder>('desc')

    const debouncedSearch = useDebounce(searchInput, 300)
    const debouncedId = useDebounce(filterById, 300)

    const filters = useMemo(() => ({
        search: debouncedSearch || undefined,
        status: statusFilter || undefined,
        partnerId: partnerFilter || undefined,
        id: debouncedId ? Number(debouncedId) : undefined,
        clientId: clientFilter || undefined,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        sortBy,
        sortOrder,
    }), [debouncedSearch, statusFilter, partnerFilter, debouncedId, clientFilter, dateRange, sortBy, sortOrder])

    // Busca os dois endpoints sem paginação de servidor (mesclar duas páginas server-side daria
    // uma lista incoerente) e pagina o resultado mesclado aqui.
    const fetchFn = useCallback(async (
        params: typeof filters & { page: number; limit: number }
    ): Promise<ApiResponse<PackageRow[]>> => {
        const { page, limit, ...rest } = params
        const query = { ...rest, packages: 'only' as const }

        const [exams, orders] = await Promise.all([
            getExamsByCompany(user!.companyId!, query),
            getOrdersByCompany(user!.companyId!, query),
        ])

        if (!exams.success || !orders.success) {
            return {
                success: false,
                result: [],
                error: exams.error || orders.error,
            }
        }

        // A ordem vem do cliente mesmo com `sortBy` na query: são duas listas
        // ordenadas em separado, e concatenar duas sequências ordenadas não
        // devolve uma sequência ordenada.
        const sortField = (row: PackageRow): string =>
            rest.sortBy === 'created'
                ? row.createdAt
                : rest.sortBy === 'updated'
                  ? row.updatedAt
                  : row.date

        // `asc` = mais antigos primeiro; `desc` (padrão) = mais recentes primeiro.
        const direction = rest.sortOrder === 'asc' ? -1 : 1

        const rows = [
            ...(exams.result || []).map(toRowFromExam),
            ...(orders.result || []).map(toRowFromOrder),
        ].sort(
            (a, b) =>
                (new Date(sortField(b)).getTime() -
                    new Date(sortField(a)).getTime()) *
                direction
        )

        const offset = (page - 1) * limit
        return {
            success: true,
            result: rows.slice(offset, offset + limit),
            total: rows.length,
        }
    }, [user?.companyId])

    const [urlPage, setUrlPage] = useUrlPage()

    const { items, loading, page, limit, total, setPage } = usePaginatedPage<PackageRow, typeof filters>({
        fetchFn,
        filters,
        limit: 15,
        enabled: !!user?.companyId,
        page: urlPage,
        onPageChange: setUrlPage,
    })

    const activeFilters = [
        searchInput,
        filterById,
        clientFilter,
        partnerFilter,
        statusFilter,
        dateRange.startDate,
    ].filter(Boolean).length

    const clearFilters = () => {
        setSearchInput('')
        setFilterById('')
        setClientFilter(null)
        setPartnerFilter(null)
        setStatusFilter('')
        setDateRange({})
    }

    if (loading && items.length === 0) {
        return (
            <div className="flex min-h-[400px] flex-col items-center justify-center gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-sm text-base-content/60">
                    Carregando pacotes...
                </p>
            </div>
        )
    }

    return (
        <div className="flex flex-col gap-5">
            <ListPageHeader
                icon={<MdInventory2 className="text-2xl" />}
                title="Pacotes"
                subtitle={`${total} ${total === 1 ? 'pacote' : 'pacotes'}`}
                search={{
                    value: searchInput,
                    onChange: setSearchInput,
                    placeholder: 'Buscar pacotes...',
                }}
                actions={
                    <>
                        {/* O agendamento abre direto na aba "Pacote" (type=PACKAGE é um ServiceType
                            válido). Mesmo par de ações da tela de Exames: orçamento e agendamento. */}
                        <button
                            onClick={() =>
                                navigate(
                                    '/atendimento/scheduling?type=PACKAGE&status=BUDGET'
                                )
                            }
                            className="btn btn-sm btn-accent btn-outline gap-2"
                        >
                            <MdAdd className="text-lg" />
                            <span className="hidden sm:inline">Orçamento</span>
                        </button>
                        {/* Pacote deixou de ser agendado: é VENDIDO no próprio cadastro,
                            com data por componente. O botão leva à lista de pacotes do
                            catálogo, onde se escolhe qual vender. */}
                        <button
                            onClick={() =>
                                navigate(
                                    '/registrations/catalog?category=PACKAGE'
                                )
                            }
                            className="btn btn-sm btn-secondary gap-2"
                        >
                            <MdSell className="text-lg" />
                            <span className="hidden sm:inline">
                                Vender pacote
                            </span>
                        </button>
                    </>
                }
            />

            <ListFilterBar activeCount={activeFilters} onClear={clearFilters}>
                <FilterField label="Número" htmlFor="package-id-filter">
                    <input
                        id="package-id-filter"
                        type="number"
                        placeholder="0"
                        value={filterById}
                        onChange={e => setFilterById(e.target.value)}
                        className="input input-bordered input-sm w-24"
                        min={1}
                    />
                </FilterField>
                <FilterField label={ClientLabel} className="w-56">
                    <ClientSelector
                        value={clientFilter}
                        onChange={id => setClientFilter(id)}
                        label=""
                        placeholder="Buscar por nome"
                        size="sm"
                    />
                </FilterField>
                <FilterField label="Status" htmlFor="package-status-filter">
                    <select
                        id="package-status-filter"
                        value={statusFilter}
                        onChange={e =>
                            setStatusFilter(
                                e.target.value as PackageStatusFilter | ''
                            )
                        }
                        className="select select-bordered select-sm w-40"
                    >
                        <option value="">Todos</option>
                        {(
                            Object.keys(
                                PACKAGE_STATUS_OPTIONS
                            ) as PackageStatusFilter[]
                        ).map(status => (
                            <option key={status} value={status}>
                                {PACKAGE_STATUS_OPTIONS[status]}
                            </option>
                        ))}
                    </select>
                </FilterField>
                {!hidePartnerSelector && (
                    <PartnerSelector
                        value={partnerFilter}
                        onChange={id => setPartnerFilter(id)}
                        label="Parceiro"
                        placeholder="Todos"
                        className="!w-48"
                        size="sm"
                    />
                )}
                <DateRangeFilter onChange={setDateRange} />
                <ListSortSelect
                    value={sortBy}
                    onChange={setSortBy}
                    order={sortOrder}
                    onOrderChange={setSortOrder}
                />
            </ListFilterBar>

            {items.length === 0 && !loading ? (
                <ListEmptyState
                    icon={<MdInventory2 />}
                    title="Nenhum pacote encontrado"
                    description={`Pacotes agendados aparecem aqui, fora das listagens de ${serviceLabels.Exams} e ${serviceLabels.Orders}.`}
                    filtered={activeFilters > 0}
                    onClearFilters={clearFilters}
                />
            ) : (
                <>
                    <div
                        className={`grid grid-cols-1 gap-4 transition-opacity md:grid-cols-2 xl:grid-cols-3 ${
                            loading ? 'pointer-events-none opacity-50' : ''
                        }`}
                    >
                        {items.map(row => (
                            <div
                                key={`${row.origin}-${row.id}`}
                                onClick={() => navigate(row.detailPath)}
                                className="group flex cursor-pointer flex-col rounded-2xl border border-base-300 bg-base-100 transition-colors hover:border-secondary/40 hover:bg-base-200/30"
                            >
                                <div className="flex flex-col gap-3 p-5">
                                    <div className="flex items-start gap-3">
                                        <h3 className="line-clamp-2 min-w-0 flex-1 text-base font-semibold text-base-content">
                                            {row.name}
                                        </h3>
                                        {/* Sem text-white forçado: o badge do tema
                                            já traz o par de cores certo. */}
                                        <span
                                            className={`badge badge-sm shrink-0 ${row.statusColor}`}
                                        >
                                            {row.statusLabel}
                                        </span>
                                    </div>

                                    <div className="flex flex-col gap-1.5">
                                        {row.clientName && (
                                            <div className="flex items-center gap-2 text-sm text-base-content/70">
                                                <MdPerson className="shrink-0 text-base text-base-content/40" />
                                                <span className="truncate">
                                                    {row.clientName}
                                                </span>
                                            </div>
                                        )}
                                        {row.partnerName && (
                                            <div className="flex items-center gap-2 text-sm text-base-content/60">
                                                <MdBusiness className="shrink-0 text-base text-base-content/40" />
                                                <span className="truncate">
                                                    {row.partnerName}
                                                </span>
                                            </div>
                                        )}
                                        {row.healthPlanName && (
                                            <div className="flex items-center gap-2 text-sm text-base-content/60">
                                                <MdHealthAndSafety className="shrink-0 text-base text-base-content/40" />
                                                <span className="truncate">
                                                    {row.healthPlanName}
                                                </span>
                                            </div>
                                        )}
                                    </div>

                                    <div className="flex flex-wrap gap-1.5">
                                        <span className="badge badge-sm badge-ghost">
                                            {row.origin === 'EXAM'
                                                ? serviceLabels.Exam
                                                : serviceLabels.Order}
                                        </span>
                                    </div>
                                </div>

                                <div className="mt-auto flex items-center justify-between gap-3 border-t border-base-300 px-5 py-3">
                                    <span className="flex items-center gap-1.5 text-xs text-base-content/50">
                                        <MdAccessTime className="text-sm" />
                                        {format(
                                            new Date(row.date),
                                            'dd/MM/yyyy',
                                            { locale: ptBR }
                                        )}
                                    </span>
                                    <span className="flex shrink-0 items-center gap-2">
                                        {row.price > 0 && (
                                            <span className="text-sm font-bold text-secondary">
                                                {(
                                                    row.price / 100
                                                ).toLocaleString('pt-BR', {
                                                    style: 'currency',
                                                    currency: 'BRL',
                                                })}
                                            </span>
                                        )}
                                        <MdArrowForward className="text-base text-base-content/30 transition-colors group-hover:text-secondary" />
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>

                    {total > limit && (
                        <PaginationControls
                            page={page}
                            limit={limit}
                            total={total}
                            loading={loading}
                            onPageChange={setPage}
                            label="pacote"
                        />
                    )}
                </>
            )}
        </div>
    )
}
